import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [tx, setTx] = useState(null);
  // checking | paid | failed
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Confirm the payment server-side. Reaching this page means Stripe has
        // already redirected after a successful charge — the server still
        // validates that the session belongs to the current customer before
        // finalising the order.
        const { data } = await api.post("/payments/confirm", { session_id: sessionId });
        if (cancelled) return;
        setTx(data);
        setStatus(data.payment_status === "paid" ? "paid" : "failed");
      } catch (e) {
        if (cancelled) return;
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setStatus("failed");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="tk-container py-24 max-w-xl text-center" data-testid="payment-status-page">
      {status === "checking" && (
        <>
          <Loader2 className="animate-spin mx-auto mb-6 text-[#0B1C30]" size={48} />
          <h1 className="text-3xl font-semibold mb-2">Bekrefter betaling …</h1>
          <p className="text-slate-500">Et øyeblikk mens vi avslutter bestillingen.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto mb-6 text-emerald-500" size={56} />
          <h1 className="text-3xl font-semibold mb-2" data-testid="payment-success-title">Takk! Betaling mottatt.</h1>
          <p className="text-slate-500 mb-2">Bestillingen er sendt til verkstedet og deler er bestilt.</p>
          {tx?.amount && (
            <p className="text-slate-400 text-sm">
              Beløp: kr {Number(tx.amount).toFixed(0)} {String(tx.currency).toUpperCase()}
            </p>
          )}
          <Link to="/mine-bestillinger" className="tk-btn-primary mt-8 inline-flex" data-testid="goto-orders-btn">
            Se mine bestillinger
          </Link>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle className="mx-auto mb-6 text-red-500" size={56} />
          <h1 className="text-3xl font-semibold mb-2">Vi fikk ikke bekreftet betalingen</h1>
          <p className="text-slate-500">
            Prøv igjen, eller kontakt{" "}
            <a href="mailto:post@tekkdeler.no" className="underline" data-testid="support-email">
              post@tekkdeler.no
            </a>
            .
          </p>
          {error && <p className="text-xs text-slate-400 mt-3">{error}</p>}
          <Link to="/mine-bestillinger" className="tk-btn-secondary mt-8 inline-flex">
            Tilbake til bestillinger
          </Link>
        </>
      )}
    </div>
  );
}
